import React, { Component } from "react";

// Redux state & action
import { connect } from "react-redux";
import projActions from "../../_actions/projActions";
import unitActions from "../../_actions/unitActions";
import { projSelectorConstants } from "../../_constants/projSelectorConstants";

// UI related
import Select from "react-select";
import ProjListSelectorModal from "./ProjListSelectorModal";

// Utility
import PropTypes from "prop-types";
import memoizeOne from "memoize-one";

/**
 * Provides a project dropdown menu, rendered inside a gray card. Users can
 * filter the projects by district, unit or user-assigned.
 * - "initProjectId" is an optional prop that sets the default selection.
 *   If one is provided, the dropdown is disabled, with the default project
 *   preselected. In this case, the dropdown is only populated with the default project.
 * - "showProjectsFor" is an optional prop that sets the default filter. Since
 *   it can be changed by the user, it is derived into the component state to
 *   allow for internal modification. Filter changes are handled through
 *   a popup modal - see ProjListSelectorModal.
 */
class ProjSelector extends Component {
  state = {
    selectedProject: null,
    /** @type {ShowProjectsFor} */
    showProjectsFor: {}, // Derived from props. See propTypes for shape
    selectedUnit: null, // If filter is UNIT, this provides unitNumber for helper caption
    showProjListSelectorModal: false,
  };

  componentDidMount() {
    const {
      user,
      tabId,
      showProjectsFor: showProjectsForFromProps,
      initProjectId,
    } = this.props;

    // Set showProjectsFor to incoming props, or a default value if none provided.
    const showProjectsFor = {
      ...(initProjectId
        ? {
            type: projSelectorConstants.SINGLE,
            lookupId: initProjectId,
          }
        : showProjectsForFromProps || {
            type: projSelectorConstants.USER_ASSIGNED,
            lookupId: user.userId,
          }),
    };

    if (showProjectsFor.type === projSelectorConstants.UNIT)
      unitActions.getAll(null, tabId);

    this.setState({ showProjectsFor }, this.refreshList);
  }

  componentDidUpdate(prevProps, prevState) {
    const { unitList } = this.props;
    const { showProjectsFor } = this.state;
    if (
      prevProps.unitList !== unitList &&
      unitList &&
      showProjectsFor.type === projSelectorConstants.UNIT
    ) {
      this.setState({
        selectedUnit: unitList.find((u) => u.unitId === showProjectsFor.lookupId) || null,
      });
    }
  }

  /**
   *
   */
  refreshList = () => {
    const { dispatch, tabId } = this.props;
    const { showProjectsFor } = this.state;
    /** @type {ProjectFilters} */
    const filters = {
      projectId:
        showProjectsFor.type === projSelectorConstants.SINGLE
          ? showProjectsFor.lookupId
          : null,
      district:
        showProjectsFor.type === projSelectorConstants.DISTRICT
          ? showProjectsFor.lookupId
          : null,
      unitId:
        showProjectsFor.type === projSelectorConstants.UNIT
          ? showProjectsFor.lookupId
          : null,
      userId:
        showProjectsFor.type === projSelectorConstants.USER_ASSIGNED
          ? showProjectsFor.lookupId
          : null,
      EA: "",
      proDMSProjectsOnly: true,
    };
    dispatch(projActions.fetchAllByFilter(filters, tabId));
  };

  updateFilter = (showProjectsFor) => {
    this.setState(
      {
        showProjectsFor,
        selectedProject: null,
        selectedUnit:
          showProjectsFor.type === projSelectorConstants.UNIT
            ? (this.props.unitList || []).find(
                (u) => u.unitId === showProjectsFor.lookupId
              ) || {}
            : null,
      },
      this.refreshList
    );
  };

  // Generates snippet caption under project select
  getHelperCaption = () => {
    const { showProjectsFor, selectedUnit } = this.state;
    const { user } = this.props;

    const baseCaption = "Showing projects";
    switch (showProjectsFor.type) {
      case projSelectorConstants.SINGLE:
        return "";
      case projSelectorConstants.DISTRICT:
        return `${baseCaption} for D${showProjectsFor.lookupId}`;
      case projSelectorConstants.UNIT:
        return `${baseCaption} for Unit ${
          selectedUnit ? selectedUnit.distUnit : "(N/A)"
        }`;
      case projSelectorConstants.USER_ASSIGNED:
        return `${baseCaption} assigned to user ${user.employeeNo}`;
      default:
        return "No Filter Selected";
    }
  };

  toggleProjListSelectorModal = () => {
    const { showProjListSelectorModal } = this.state;
    const { dispatch, tabId } = this.props;
    if (!showProjListSelectorModal) dispatch(unitActions.getAll(null, tabId));
    this.setState({
      showProjListSelectorModal: !showProjListSelectorModal,
    });
  };

  handleChange_project = (selectedProject) => {
    this.setState({ selectedProject });
    this.props.onProjectSelect(selectedProject.value);
  };

  memoizedProjectOptions = memoizeOne((projList) =>
    (projList || []).map((p) => ({
      value: p.projectId,
      label: p.fullEA + ", " + p.workDescription,
    }))
  );

  memoizedHelperCaption = memoizeOne();

  render() {
    const { selectedProject, showProjectsFor, showProjListSelectorModal } = this.state;
    const { tabId, initProjectId, resetProjectId, projList } = this.props;

    /** @type {SelectOption[]} */
    const projectOptions = this.memoizedProjectOptions(projList);

    return (
      <React.Fragment>
        <div className="card mx-2 bg-lightgray">
          <div className="card-body pb-0">
            <div className="form-row form-group">
              <label className="col-sm-3 col-form-label">
                <strong>Project EA:*</strong>
              </label>
              <div className="col-sm-9">
                <Select
                  name="projectId"
                  id="projectId"
                  value={
                    initProjectId
                      ? projectOptions.find((o) => o.value === initProjectId)
                      : selectedProject
                  }
                  options={projectOptions}
                  onChange={this.handleChange_project}
                  placeholder="Select a Project"
                  isDisabled={initProjectId}
                />
                {!selectedProject && !initProjectId && (
                  <span className="error-small mr-2 align-middle">
                    <small>No project selected</small>
                  </span>
                )}
                {!initProjectId && (
                  <React.Fragment>
                    <span className="align-middle">
                      <small>{`${this.getHelperCaption()} `}</small>
                    </span>
                    <button
                      type="button"
                      name="moreProjects"
                      id="moreProjects"
                      className="btn btn-sm btn-link p-0 align-middle"
                      onClick={this.toggleProjListSelectorModal}
                    >
                      <small>More Projects...</small>
                    </button>
                    {/* <a href="#" onClick={this.toggleProjListSelectorModal}>
                        More Projects...
                      </a> */}
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
        {showProjListSelectorModal && (
          <ProjListSelectorModal
            tabId={tabId}
            showProjListSelectorModal={showProjListSelectorModal}
            toggleProjListSelectorModal={this.toggleProjListSelectorModal}
            showProjectsFor={showProjectsFor}
            updateFilter={this.updateFilter}
            resetProjectId={resetProjectId}
            refreshProjectOptions={this.refreshList}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  projList: state.projects.openProjectLists[ownProps.tabId] || null,
  user: state.authentication.user,
  unitList: state.users.openUnitLists[ownProps.tabId] || null,
});

ProjSelector.propTypes = {
  // tabId is used for indexing into normalized redux storage.
  // When this component is used as an intermediate input step prior to
  // opening a tab, then modalTabIds are to be used.
  tabId: PropTypes.number,
  // projectId represents the current selected projectId.
  // Updates whenever new project is selected (see onProjectSelect prop).
  // projectId: PropTypes.number,
  // initProjectId allows pre-selection of project. Immutable.
  initProjectId: PropTypes.number,
  // showProjectsFor packages parameters required for pre-populating
  // the projects list. Once the component is mounted, this prop is
  // copied into derived state, to be manipulated by ProjListSelectorModal.
  showProjectsFor: PropTypes.shape({
    type: PropTypes.string, // One of projSelectorConstants
    lookupId: PropTypes.number, // int
  }),
  // Change handler, receives a single parameter of 'projectId'.
  onProjectSelect: PropTypes.func,
  // Resets projectId to 0 after modifying projListSelector.
  resetProjectId: PropTypes.func,
};

export default connect(mapStateToProps)(ProjSelector);
